import { useCallback, useEffect, useRef, useState } from "react";
import { useClipboard } from "./use-clipboard";

interface ShareData {
  title: string;
  text?: string;
  url: string;
}

/** Discriminates how the last share was delivered. Auto-resets to `false` after 2 seconds. */
export type ShareMethod = false | "native" | "clipboard";

interface UseShareReturn {
  /** Trigger a share. Uses the Web Share API when available, otherwise copies the URL to the clipboard. */
  share: (data: ShareData) => Promise<void>;
  /** How the most recent share was delivered. Resets to `false` after 2 s. */
  shared: ShareMethod;
}

const RESET_TIMEOUT = 2000;

/**
 * Progressive-enhancement share hook.
 *
 * Tries `navigator.share()` first (Chrome, Safari, Edge — desktop & mobile).
 * Falls back to copying the URL to the clipboard (Firefox desktop).
 */
export function useShare(): UseShareReturn {
  const [canNativeShare] = useState(() => typeof navigator !== "undefined" && typeof navigator.share === "function");
  const { copy } = useClipboard();
  const [shared, setShared] = useState<ShareMethod>(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const markShared = useCallback((method: "native" | "clipboard") => {
    clearTimeout(timerRef.current);
    setShared(method);
    timerRef.current = setTimeout(() => setShared(false), RESET_TIMEOUT);
  }, []);

  const share = useCallback(
    async (data: ShareData): Promise<void> => {
      if (canNativeShare) {
        try {
          await navigator.share(data);
          markShared("native");
          return;
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            return;
          }
        }
      }

      const result = await copy(data.url);
      if (result.success) {
        markShared("clipboard");
      }
    },
    [canNativeShare, copy, markShared],
  );

  return { share, shared };
}
